import React, { useState } from "react";

import { strings } from "@perceptron/constants";
import { cn } from "@perceptron/utils/cn";
import { getContentTypeFromExtension } from "@perceptron/utils/mime";
import { MediaKind } from "@perceptron/utils/types";
import { MediaPlayer, MediaProvider, VideoMimeType } from "@vidstack/react";

import { MediaFactory } from "@demo/libs/media-factory";
import { MediaData } from "@demo/types/base";

import { ImageViewer } from "./ImageViewer";

interface MediaContentProps {
  media: MediaData;
  mediaIndex: number;
  onMediaClick: (e: React.MouseEvent) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Renders media content (image or video) based on content type
 */
export function MediaContent({
  media,
  mediaIndex,
  onMediaClick,
  onLoadingChange,
  className = "",
  children,
}: MediaContentProps) {
  const [isLoading, setIsLoading] = useState(true);
  const mediaKind = MediaFactory.detectMediaKind(media);

  const handleLoadingChange = (loading: boolean) => {
    setIsLoading(loading);
    onLoadingChange?.(loading);
  };

  if (mediaKind === MediaKind.Video) {
    if (!media?.url) {
      return (
        <div className="flex h-full w-full items-center justify-center bg-gray-200 text-xs text-gray-500">
          {strings.videoRenderer.invalidVideo}
        </div>
      );
    }

    return (
      <div className={cn("relative", className)}>
        <MediaPlayer
          controls
          muted
          preload="metadata"
          src={{
            src: media.url,
            type: getContentTypeFromExtension(media.extension) as VideoMimeType,
          }}
        >
          <MediaProvider />
        </MediaPlayer>
      </div>
    );
  }

  if (mediaKind === MediaKind.Image) {
    return (
      <div className={cn("relative h-full w-full", className)}>
        {isLoading && <div className="absolute inset-0 z-10 bg-black" />}
        <ImageViewer
          media={media}
          mediaIndex={mediaIndex}
          onImageClick={onMediaClick}
          onLoadingChange={handleLoadingChange}
        >
          {children}
        </ImageViewer>
      </div>
    );
  }

  // Fallback for unsupported media types
  return (
    <div className={cn("flex h-[300px] w-full items-center justify-center bg-gray-800 text-white", className)}>
      <div className="text-center">
        <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
        <p className="mt-2 text-sm">{strings.mediaCarousel.errors.unsupportedMediaFormat}</p>
      </div>
    </div>
  );
}
